import { useState, useEffect, useCallback } from "react";
import { authFetch, useAuth } from "../../App";
import { offlineAuthFetch } from "../../lib/offlineAuthFetch";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import DayForm from "../../components/DayForm";

const API = "/api";

const hm = (m) => {
  const v = Math.abs(m || 0);
  return `${Math.floor(v / 60)}:${(v % 60).toString().padStart(2, "0")}`;
};

function getStatusInfo(diff, soll) {
  if (soll === 0) return { color: "#9CA3AF", bg: "rgba(156,163,175,0.1)", border: "rgba(156,163,175,0.2)", label: "Kein Soll" };
  if (diff >= 0) return { color: "#4ADE80", bg: "rgba(74,222,128,0.1)", border: "rgba(74,222,128,0.2)", label: "Im Plan" };
  const pct = soll > 0 ? (diff / soll) : 0;
  if (pct >= -0.15) return { color: "#FBBF24", bg: "rgba(251,191,36,0.1)", border: "rgba(251,191,36,0.2)", label: "Leicht drunter" };
  return { color: "#F87171", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.2)", label: "Hinterher" };
}

export default function MobileEingabe() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  // selectedDate default = heute (ISO yyyy-MM-dd)
  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), "yyyy-MM-dd"));

  const loadStats = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/timesheet/my-today`);
      if (res.ok) setData(await res.json());
    } catch {}
  }, []);

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const r = await offlineAuthFetch(`${API}/projects`, {}, { cacheKey: "projects-list" });
      if (r.ok) {
        const list = await r.json();
        const allowed = new Set(["aktiv", "in_planung"]);
        const filtered = (list || [])
          .filter(p => p && (p.status ? allowed.has(p.status) : p.is_active !== false))
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "de", { sensitivity: "base" }));
        setProjects(filtered);
      }
    } catch {}
    setProjectsLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadProjects()]);
      setLoading(false);
    };
    init();
  }, [loadStats, loadProjects]);

  useEffect(() => {
    const handler = () => loadStats();
    window.addEventListener("stundenkonto-refresh", handler);
    return () => window.removeEventListener("stundenkonto-refresh", handler);
  }, [loadStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-[#0f1117]">
        <Loader2 className="w-6 h-6 text-gray-500 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-4 py-8 text-center text-gray-500 text-sm min-h-screen bg-[#0f1117]" data-testid="m-stundenkonto">
        Keine Daten verfügbar
      </div>
    );
  }

  const { today: td, week: wk, is_nachunternehmer } = data;
  const todayDiff = td.diff_minutes;
  const todaySoll = td.soll_minutes;
  const todayIst = td.ist_minutes;
  const todayStatus = getStatusInfo(todayDiff, todaySoll);
  const isAbsence = td.absence_type && td.absence_type !== "arbeitstag";
  const absLabel = td.absence_type === "urlaub" ? "Urlaub" : td.absence_type === "krank" ? "Krank" : td.absence_type === "feiertag" ? "Feiertag" : td.absence_type === "frei" ? "Frei" : null;

  const selectedWeekDay = wk.days.find(d => d.date === selectedDate) || null;

  return (
    <motion.div
      className="px-4 py-4 space-y-4 min-h-screen bg-[#0f1117] text-white pb-24 w-full max-w-full overflow-x-hidden"
      data-testid="m-stundenkonto"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* TODAY CARD */}
      <motion.div
        className="rounded-xl border-2 p-4 relative overflow-hidden"
        style={{ backgroundColor: todayStatus.bg, borderColor: todayStatus.border }}
        data-testid="m-today-card"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, delay: 0.1 }}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-gray-400">Heute</div>
            <div className="text-[11px] text-gray-500">{format(new Date(), "EEEE, dd. MMMM", { locale: de })}</div>
          </div>
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{ backgroundColor: todayStatus.color + "18" }}
            data-testid="m-today-status"
          >
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: todayStatus.color }} />
            <span className="text-[11px] font-semibold" style={{ color: todayStatus.color }}>
              {isAbsence ? absLabel : todayStatus.label}
            </span>
          </div>
        </div>

        {isAbsence ? (
          <div className="text-center py-4">
            <span className="text-lg font-semibold" style={{ color: todayStatus.color }}>{absLabel}</span>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center min-w-0">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Gearbeitet</div>
              <div className="text-xl sm:text-2xl font-bold text-white font-mono tabular-nums">{hm(todayIst)}</div>
            </div>
            {!is_nachunternehmer && (
              <div className="text-center min-w-0">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Soll</div>
                <div className="text-xl sm:text-2xl font-bold text-gray-400 font-mono tabular-nums">{hm(todaySoll)}</div>
              </div>
            )}
            {!is_nachunternehmer && (
              <div className="text-center min-w-0">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Differenz</div>
                <div className="text-xl sm:text-2xl font-bold font-mono tabular-nums" style={{ color: todayStatus.color }}>
                  {todayDiff >= 0 ? "+" : ""}{hm(todayDiff)}
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* TAGES-FORMULAR — immer sichtbar, groß, übersichtlich */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <DayForm
          key={selectedDate}
          date={selectedDate}
          weekDay={selectedWeekDay}
          projects={projects}
          projectsLoading={projectsLoading}
          onPrevDay={() => {
            const d = new Date(selectedDate);
            d.setDate(d.getDate() - 1);
            setSelectedDate(format(d, "yyyy-MM-dd"));
          }}
          onNextDay={() => {
            const today = new Date();
            const d = new Date(selectedDate);
            d.setDate(d.getDate() + 1);
            // Nur bis heute
            if (d <= today) setSelectedDate(format(d, "yyyy-MM-dd"));
          }}
          canNext={selectedDate < format(new Date(), "yyyy-MM-dd")}
        />
      </motion.div>
    </motion.div>
  );
}
